import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Coins, Sparkles, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import AnimatedBadge from './AnimatedBadge';
import type { BadgeTier } from '../lib/badges';

interface OrderCelebrationProps {
  /** Projected coins this order will earn on delivery (0 → no coins line). */
  earnedCoins: number;
  /** The customer's current rank badge, or null if they have none yet. */
  tier: BadgeTier | null;
  /** Dismiss the overlay (parent should stop rendering it). */
  onClose: () => void;
}

const CONFETTI_COLORS = ['#34d399', '#f59e0b', '#ec4899', '#38bdf8', '#a78bfa', '#f43f5e', '#facc15'];

/**
 * Full-screen order-placed celebration: a ribbon, hand-rolled framer-motion
 * confetti ("flitters"), and a count-up of the coins the order will earn once
 * delivered. Respects reduced-motion (skips particles/count-up, keeps the
 * message). The parent mounts it inside an <AnimatePresence> so the exit plays.
 */
export default function OrderCelebration({ earnedCoins, tier, onClose }: OrderCelebrationProps) {
  const { isDark } = useTheme();
  const reduce = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const animate = mounted && !reduce;

  // Confetti particles — generated once so they stay stable across re-renders.
  const particles = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 6 + Math.random() * 8,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        delay: Math.random() * 0.6,
        duration: 2.4 + Math.random() * 1.6,
        rotate: Math.random() * 360,
        drift: (Math.random() - 0.5) * 120,
        round: Math.random() > 0.5,
      })),
    [],
  );

  // Count-up for the coin figure.
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (earnedCoins <= 0) {
      setShown(0);
      return;
    }
    if (reduce) {
      setShown(earnedCoins);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const dur = 900;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(eased * earnedCoins));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [earnedCoins, reduce]);

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      {/* confetti layer */}
      {animate && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {particles.map((p) => (
            <motion.div
              key={p.id}
              className="absolute"
              style={{
                left: `${p.left}%`,
                width: p.size,
                height: p.size,
                backgroundColor: p.color,
                borderRadius: p.round ? '9999px' : '2px',
              }}
              initial={{ y: '-10vh', x: 0, rotate: 0, opacity: 1 }}
              animate={{ y: '110vh', x: p.drift, rotate: p.rotate + 360, opacity: [1, 1, 0.9, 0] }}
              transition={{ delay: p.delay, duration: p.duration, ease: 'easeIn', repeat: Infinity, repeatDelay: 0.4 }}
            />
          ))}
        </div>
      )}

      {/* card */}
      <motion.div
        className={`relative w-full max-w-md rounded-3xl border p-8 text-center shadow-2xl ${
          isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'
        }`}
        initial={{ opacity: 0, scale: 0.85, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className={`absolute right-4 top-4 rounded-full p-1.5 ${
            isDark ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'
          }`}
        >
          <X size={18} />
        </button>

        {/* ribbon */}
        <motion.div
          className="mx-auto mb-5 inline-block rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-1.5 text-sm font-extrabold uppercase tracking-wider text-white shadow-lg"
          initial={{ y: -40, opacity: 0, rotate: -4 }}
          animate={{ y: 0, opacity: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 14, delay: 0.1 }}
        >
          Order Placed! 🎉
        </motion.div>

        {tier && (
          <div className="mb-4 flex justify-center">
            <AnimatedBadge src={tier.src} glow={tier.glow} size={92} variant="full" alt={tier.name} />
          </div>
        )}

        <h2 className={`text-2xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'}`}>Thank you!</h2>

        {earnedCoins > 0 ? (
          <div className="mt-4">
            <div className="inline-flex items-center gap-2 rounded-2xl bg-amber-500/10 px-4 py-3">
              <Coins size={22} className="text-amber-500" />
              <span className="text-3xl font-black tabular-nums text-amber-500">{shown}</span>
              <span className="text-lg font-bold text-amber-500">ZS Coins</span>
            </div>
            <p className={`mt-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              You'll earn these when your order is <strong>delivered</strong>.
            </p>
          </div>
        ) : (
          <p className={`mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            We'll send you a confirmation email shortly.
          </p>
        )}

        <div className="mt-7 flex flex-col gap-2">
          <Link
            to="/store"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-emerald-700"
          >
            Continue Shopping
          </Link>
          <Link
            to="/rewards"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-emerald-500 hover:underline"
          >
            <Sparkles size={14} /> View my rewards
          </Link>
        </div>
      </motion.div>
    </motion.div>
  );
}
